import { PropsWithChildren, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useGridStackContext } from './grid-stack-context';
import { GridStack, GridStackOptions, GridStackWidget } from 'gridstack';
import { GridStackRenderContext } from './grid-stack-render-context';
import isEqual from 'react-fast-compare';

interface GridStackRenderProviderProps extends PropsWithChildren {
  width?: number; // Optional explicit width for canvas
  showGridLines?: boolean; // When true, grid lines are visible (e.g. when dragging from drawer)
}

export function GridStackRenderProvider({ children, width, showGridLines = false }: GridStackRenderProviderProps) {
  const {
    _gridStack: { value: gridStack, set: setGridStack },
    initialOptions,
  } = useGridStackContext();

  const widgetContainersRef = useRef<Map<string, HTMLElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<GridStackOptions>(initialOptions);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const renderCBFn = useCallback((element: HTMLElement, widget: GridStackWidget) => {
    if (widget.id) {
      widgetContainersRef.current.set(widget.id, element);
    }
  }, []);

  const initGrid = useCallback(() => {
    const container = containerRef.current;
    if (!container) return null;

    // Set explicit width if provided (for fixed canvas)
    if (width) {
      container.style.width = `${width}px`;
    }

    const measured = container.offsetWidth;
    if (measured === 0) return null;

    GridStack.renderCB = renderCBFn;
    const instance = GridStack.init(optionsRef.current, container);

    const columnCount = optionsRef.current.column;
    const numColumns = typeof columnCount === 'number' ? columnCount : 12;
    if (typeof instance.column === 'function') {
      instance.column(numColumns, 'none');
    }

    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        // ResizeObserver ensures layout updates are handled
      });
      resizeObserverRef.current.observe(container);
    }

    return instance;
    // ! Change event not firing on nested grids (resize, move...) https://github.com/gridstack/gridstack.js/issues/2671
  }, [renderCBFn, width]);

  useLayoutEffect(() => {
    if (!isEqual(initialOptions, optionsRef.current) && gridStack) {
      try {
        gridStack.removeAll(false);
        gridStack.destroy(false);
        widgetContainersRef.current.clear();
        optionsRef.current = initialOptions;
        setGridStack(initGrid());
      } catch (e) {
        console.error('Error reinitializing gridstack', e);
      }
    }
  }, [initialOptions, gridStack, initGrid, setGridStack]);

  useLayoutEffect(() => {
    if (!gridStack) {
      try {
        setGridStack(initGrid());
      } catch (e) {
        console.error('Error initializing gridstack', e);
      }
    }
  }, [gridStack, initGrid, setGridStack]);

  // Cleanup ResizeObserver
  useLayoutEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, []);

  return (
    <GridStackRenderContext.Provider
      value={useMemo(
        () => ({
          getWidgetContainer: (widgetId: string) => {
            return widgetContainersRef.current.get(widgetId) || null;
          },
        }),
        // ! gridStack is required to reinitialize the grid when the options change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [gridStack]
      )}
    >
      <div
        ref={containerRef}
        className={`grid-stack${showGridLines ? ' grid-stack-show-grid' : ''}`}
        style={width ? { width: `${width}px` } : undefined}
      >
        {gridStack ? children : null}
      </div>
    </GridStackRenderContext.Provider>
  );
}
